import { useState } from "react";
import { useDispatch } from "react-redux";

import { Switch, Tooltip, Typography } from "antd";
import { EvaChart } from "constants/charts.enums";
import { useChartsContext } from "contexts/ChartContext";
import { updateMultiProductProperty } from "store/features/charts/mpcActions";
import styled from "styled-components";

import { useChartEntities } from "hooks/charts";

import { ChevronDown } from "components/icons";
import SegmentedToggle from "components/shared/SegmentedToggle";

const { Text } = Typography;

interface MultiProductOptionsProps {
  isMultiProductActive: boolean;
  onToggleMultiProduct: (active: boolean) => void;
  disabled?: boolean;
}

export function MultiProductOptions({
  isMultiProductActive,
  onToggleMultiProduct,
  disabled = false
}: MultiProductOptionsProps) {
  const dispatch = useDispatch();
  const { id } = useChartsContext();
  const { multiProduct, chartType } = useChartEntities(id);

  // The override toggle shows for every MPC chart; the axis rows are RateCum/TotalRateCum-only.
  const canShowSettings = isMultiProductActive;
  const canShowAxisSettings =
    isMultiProductActive &&
    (chartType === EvaChart.RateCum || chartType === EvaChart.TotalRateCum);
  const [isSettingsOpen, setIsSettingsOpen] = useState(true);
  const combineSameUnitAxis = multiProduct?.properties?.combineSameUnitAxis ?? false;
  const showProductLegend = multiProduct?.properties?.showProductLegend ?? false;
  const isMBblAxisPositionBottom =
    multiProduct?.properties?.isMBblAxisPositionBottom ?? true;
  const isMMcfAxisPositionBottom =
    multiProduct?.properties?.isMMcfAxisPositionBottom ?? true;
  const overrideBinColors = multiProduct?.properties?.overrideBinColors ?? false;

  const handleCombineToggle = (active: boolean) => {
    dispatch(updateMultiProductProperty(id, "combineSameUnitAxis", active));
  };

  const handleProductLegendToggle = (active: boolean) => {
    dispatch(updateMultiProductProperty(id, "showProductLegend", active));
  };

  const handleMBblPositionChange = (isBottom: boolean) => {
    dispatch(updateMultiProductProperty(id, "isMBblAxisPositionBottom", isBottom));
  };

  const handleMMcfPositionChange = (isBottom: boolean) => {
    dispatch(updateMultiProductProperty(id, "isMMcfAxisPositionBottom", isBottom));
  };

  const handleOverrideBinColorsToggle = (active: boolean) => {
    dispatch(updateMultiProductProperty(id, "overrideBinColors", active));
    // Every override session starts with all series solid, and the exceptions a
    // user picked during one don't outlive it — the picks themselves survive in
    // productLineStyles, which is what the chart falls back to (EVA-56).
    dispatch(updateMultiProductProperty(id, "overrideLineStyles", {}));
  };

  return (
    <>
      <MultiProductOptionsContainer $MultiProdActive={isMultiProductActive}>
        <Row
          onClick={() => canShowSettings && setIsSettingsOpen((prev) => !prev)}
          $isCollapse={canShowSettings}>
          {canShowSettings && (
            <ChevronButton $isOpen={isSettingsOpen}>
              <ChevronDown size={22} />
            </ChevronButton>
          )}
          Multi Product Mode
        </Row>
        <Tooltip
          title={disabled ? "Multi product mode is not supported for this chart." : ""}>
          <Switch
            checked={isMultiProductActive}
            onChange={(checked) => onToggleMultiProduct(checked)}
            size="small"
            disabled={disabled}
          />
        </Tooltip>
      </MultiProductOptionsContainer>

      {canShowSettings && isSettingsOpen && (
        <SettingsContainer>
          <SettingsTitle>Multi Product Settings</SettingsTitle>
          <Row>
            <Text>Product Legend</Text>
            <Switch
              data-testid="product-legend-switch"
              size="small"
              checked={showProductLegend}
              onChange={handleProductLegendToggle}
            />
          </Row>

          <Row>
            <Text>Override Bin Colours</Text>
            <Switch
              data-testid="override-bin-colors-switch"
              size="small"
              checked={overrideBinColors}
              onChange={handleOverrideBinColorsToggle}
            />
          </Row>

          {canShowAxisSettings && (
            <>
              <Row>
                <Text>Merge Same Unit Axes</Text>
                <Switch
                  data-testid="combine-same-unit-axis-switch"
                  size="small"
                  checked={combineSameUnitAxis}
                  onChange={handleCombineToggle}
                />
              </Row>

              <Row>
                <Text>Mbbl Axis Position</Text>
                <SegmentedToggle
                  size="small"
                  options={["Top", "Bottom"]}
                  value={isMBblAxisPositionBottom ? "Bottom" : "Top"}
                  onChange={(val) => handleMBblPositionChange(val === "Bottom")}
                />
              </Row>

              <Row>
                <Text>MMcf Axis Position</Text>
                <SegmentedToggle
                  size="small"
                  options={["Top", "Bottom"]}
                  value={isMMcfAxisPositionBottom ? "Bottom" : "Top"}
                  onChange={(val) => handleMMcfPositionChange(val === "Bottom")}
                />
              </Row>
            </>
          )}
        </SettingsContainer>
      )}
    </>
  );
}

const ChevronButton = styled.div<{ $isOpen: boolean }>`
  display: flex;
  cursor: pointer;
  transition: all var(--duration-short) ease;
  transform: rotate(${({ $isOpen }) => ($isOpen ? "0deg" : "-90deg")});
`;

const MultiProductOptionsContainer = styled.div<{ $MultiProdActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 15px;
  font-weight: bold;
  font-size: 1.4rem;
  border-bottom: 1px solid #d9e1e2;
  transition: all var(--duration-short) ease;
  ${({ $MultiProdActive }) => ($MultiProdActive ? `background: #ccfbf1;` : null)}
`;

const Row = styled.div<{ $isCollapse?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: default;

  ${({ $isCollapse }) =>
    $isCollapse
      ? `
    cursor: pointer;
    
    &:hover svg {
      transition: all var(--duration-short) ease;
      color: var(--color-primary);
    }
  `
      : null}
`;

const SettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 15px 15px;
  gap: 6px;
  background: var(--color-primary-06);
  border-bottom: 1px solid #d9e1e2;

  ${Row} {
    margin-left: 12px;
    margin-right: 12px;
    cursor: default;
  }
`;

const SettingsTitle = styled.div`
  font-weight: bold;
  font-size: 1.4rem;
  cursor: default;
`;
